import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, Download, Image as ImageIcon, Video, FileText, Palette, Type, Package, Sparkles, FolderOpen, Layers, Folder } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmProvider";

const FOLDER_META = {
  "Logos": { icon: Sparkles, tint: "from-amber-500/30 to-orange-500/20 border-amber-500/30 text-amber-700" },
  "Brand Guidelines": { icon: FileText, tint: "from-orange-500/30 to-red-500/20 border-orange-500/30 text-orange-700" },
  "Photos": { icon: ImageIcon, tint: "from-emerald-500/30 to-cyan-500/20 border-emerald-500/30 text-emerald-700" },
  "Videos": { icon: Video, tint: "from-rose-500/30 to-red-500/20 border-rose-500/30 text-rose-700" },
  "Previous Creatives": { icon: Layers, tint: "from-red-500/30 to-fuchsia-500/20 border-red-500/30 text-red-700" },
  "Templates": { icon: FolderOpen, tint: "from-blue-500/30 to-sky-500/20 border-blue-500/30 text-blue-700" },
  "Fonts": { icon: Type, tint: "from-neutral-500/30 to-neutral-400/20 border-neutral-500/30 text-neutral-800" },
  "Product Images": { icon: Package, tint: "from-teal-500/30 to-emerald-500/20 border-teal-500/30 text-teal-700" },
  "Campaign Assets": { icon: Palette, tint: "from-pink-500/30 to-rose-500/20 border-pink-500/30 text-pink-700" },
};
const ALL_FOLDERS = Object.keys(FOLDER_META);

function isImage(ct) { return (ct||"").startsWith("image/"); }
function isVideo(ct) { return (ct||"").startsWith("video/"); }
function fmtSize(n) {
  if (!n) return "";
  const kb = n/1024; if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb/1024).toFixed(1)} MB`;
}

export default function LibraryPanel({ clientId, clientName }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState("Logos");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/library`);
      setFiles(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(()=>{ if (clientId) load(); }, [clientId]);

  const counts = useMemo(() => {
    const m = {}; ALL_FOLDERS.forEach(f=>m[f]=0);
    files.forEach(f=>{ m[f.folder] = (m[f.folder]||0)+1; });
    return m;
  }, [files]);
  const active_files = files.filter(f=>f.folder===active);

  const canDelete = (f) => ["admin", "ceo", "coo", "manager"].includes(user?.role) || (user?.role === "client" ? f.uploaded_by === user.id : true);

  const remove = async (f) => {
    if (!(await confirm({ title: "Are you sure you want to delete this file?", description: `"${f.filename}" will be removed from the ${f.folder} folder. This cannot be undone.` }))) return;
    try { await api.delete(`/library/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ActiveIcon = (FOLDER_META[active]?.icon) || Folder;

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      {/* Folder sidebar */}
      <aside className="lg:col-span-3 panel p-3 h-fit">
        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 px-2 pb-2">Folders</div>
        <div className="space-y-1">
          {ALL_FOLDERS.map(f => {
            const Icon = FOLDER_META[f].icon;
            const activeCls = active === f ? "bg-orange-100 text-orange-700 font-semibold border-orange-500/40" : "text-neutral-700 hover:bg-white/[0.03] border-transparent";
            return (
              <button key={f} onClick={()=>setActive(f)} className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm border transition-colors ${activeCls}`} data-testid={`lib-folder-${f.toLowerCase().replace(/\s+/g,'-')}`}>
                <Icon className="w-4 h-4 shrink-0"/>
                <span className="flex-1 text-left truncate">{f}</span>
                <span className="text-[10px] font-mono text-neutral-500">{counts[f]||0}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* File grid */}
      <section className="lg:col-span-9 panel p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${FOLDER_META[active].tint} border grid place-items-center`}>
              <ActiveIcon className="w-5 h-5"/>
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight">{active}</div>
              <div className="text-[10px] font-mono uppercase text-neutral-500 tracking-wider">
                {active_files.length} {active_files.length===1?"file":"files"}{clientName && ` · ${clientName}`}
              </div>
            </div>
          </div>
          <Button onClick={()=>setUploadOpen(true)} data-testid="lib-upload-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Upload className="w-4 h-4 mr-1"/> Upload to {active}
          </Button>
        </div>

        {active_files.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#E4E7EC] p-10 text-center">
            <div className="inline-flex w-12 h-12 rounded-full bg-white/[0.03] border border-[#E4E7EC] grid place-items-center mb-2">
              <ActiveIcon className="w-5 h-5 text-neutral-600"/>
            </div>
            <div className="text-sm font-semibold">Nothing here yet</div>
            <div className="text-xs text-neutral-500 mt-1">Upload your first {active.toLowerCase()} to get organised.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {active_files.map(f => (
              <div key={f.id} className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] overflow-hidden group" data-testid={`lib-file-${f.id}`}>
                <button onClick={()=>setPreview(f)} className="w-full block aspect-video bg-[#FFFFFF] relative">
                  {isImage(f.content_type) ? (
                    <img src={fileUrl(f.storage_path)} alt={f.filename} className="w-full h-full object-cover"/>
                  ) : isVideo(f.content_type) ? (
                    <div className="w-full h-full grid place-items-center text-neutral-500 relative">
                      <Video className="w-8 h-8"/>
                      <span className="absolute bottom-1 right-1 text-[9px] font-mono uppercase text-neutral-600">Video</span>
                    </div>
                  ) : (
                    <div className="w-full h-full grid place-items-center text-neutral-500">
                      <FileText className="w-8 h-8"/>
                    </div>
                  )}
                </button>
                <div className="p-3">
                  <div className="text-xs font-medium truncate" title={f.filename}>{f.filename}</div>
                  <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-neutral-500">
                    <span>{fmtSize(f.size)}</span>
                    <span>{(f.created_at||"").slice(0,10)}</span>
                  </div>
                  {f.uploader_name && <div className="text-[10px] font-mono text-neutral-500 mt-0.5 truncate">by {f.uploader_name}</div>}
                  <div className="mt-2 flex gap-1">
                    <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="flex-1 text-[10px] font-mono uppercase tracking-wider text-orange-700 hover:text-orange-800 border border-orange-500/30 rounded py-1 text-center" data-testid={`lib-download-${f.id}`}>
                      <Download className="w-3 h-3 inline mr-1"/>Open
                    </a>
                    {canDelete(f) && (
                      <button onClick={()=>remove(f)} className="text-[10px] px-2 py-1 rounded border border-rose-500/30 text-rose-700 hover:bg-rose-500/10" data-testid={`lib-delete-${f.id}`}>
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} clientId={clientId} folder={active} onSaved={load}/>
      <PreviewDialog file={preview} onOpenChange={(o)=>{ if(!o) setPreview(null); }}/>
    </div>
  );
}

function UploadDialog({ open, onOpenChange, clientId, folder, onSaved }) {
  const fileRef = useRef(null);
  const [pickedName, setPickedName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [chosenFolder, setChosenFolder] = useState(folder);
  useEffect(()=>{ if (open) { setPickedName(""); setNotes(""); setChosenFolder(folder); if (fileRef.current) fileRef.current.value = ""; } }, [open, folder]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", chosenFolder);
      fd.append("notes", notes);
      await api.post(`/clients/${clientId}/library`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-lg" data-testid="lib-upload-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Upload className="w-5 h-5 text-orange-400"/>Upload to Library</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Folder</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {ALL_FOLDERS.map(f => (
                <button key={f} onClick={()=>setChosenFolder(f)} className={`text-[10px] font-mono uppercase px-2 py-1.5 rounded border transition-colors ${chosenFolder===f?"border-orange-500 bg-orange-100 text-orange-700 font-semibold":"border-[#E4E7EC] text-neutral-600 hover:text-neutral-800"}`} data-testid={`lib-upload-folder-${f.toLowerCase().replace(/\s+/g,'-')}`}>
                  {f}
                </button>
              ))}
            </div>
            <div className="text-[11px] font-mono text-neutral-600 mt-1.5" data-testid="lib-upload-selected-folder">Selected folder: <span className="text-orange-700 font-semibold">{chosenFolder}</span></div>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">File</Label>
            <input ref={fileRef} type="file" onChange={(e)=>setPickedName(e.target.files?.[0]?.name||"")} className="mt-1 w-full text-sm text-neutral-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-orange-500 file:text-neutral-900 file:cursor-pointer" data-testid="lib-file-input"/>
            {pickedName && <div className="text-[11px] font-mono text-neutral-600 mt-1">Selected: {pickedName}</div>}
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} data-testid="lib-notes" className="bg-[#FFFFFF] border-[#E4E7EC] mt-1" placeholder="Context, campaign, version..."/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="lib-upload-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lib-upload-save">
            {busy?"Uploading...":"Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ file, onOpenChange }) {
  const open = !!file;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-3xl" data-testid="lib-preview-dialog">
        {file && (
          <>
            <DialogHeader><DialogTitle className="text-lg font-bold truncate">{file.filename}</DialogTitle></DialogHeader>
            <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] overflow-hidden">
              {isImage(file.content_type) ? (
                <img src={fileUrl(file.storage_path)} alt={file.filename} className="w-full max-h-[70vh] object-contain"/>
              ) : isVideo(file.content_type) ? (
                <video src={fileUrl(file.storage_path)} controls className="w-full max-h-[70vh]"/>
              ) : (
                <div className="p-10 text-center text-neutral-600">
                  <FileText className="w-10 h-10 mx-auto mb-2"/>
                  <div>Preview not available for this file type.</div>
                </div>
              )}
            </div>
            <div className="text-[11px] font-mono text-neutral-500 flex flex-wrap gap-4 mt-2">
              <span>{file.folder}</span>
              <span>{fmtSize(file.size)}</span>
              <span>{file.content_type}</span>
              <span>by {file.uploader_name || "—"}</span>
              <span>{(file.created_at||"").slice(0,10)}</span>
            </div>
            {file.notes && <div className="text-sm text-neutral-700 mt-1 whitespace-pre-wrap">{file.notes}</div>}
            <DialogFooter>
              <a href={fileUrl(file.storage_path)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#E4E7EC]"><Download className="w-4 h-4 mr-1"/>Open in new tab</Button>
              </a>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
